import { ZRoomSyncPlan, ZMlCeilingRoomNumbers } from "./roomSyncPlan.schema";
import { z } from "zod";
import { ZMongoId, ZProjectNo } from "./foundationSchema";
import { ZDateTime } from "./room.schema";
import { ZJobType } from "./enrichJob.schema";
export const ZRoomSyncStats = z.object({
    stats: z.object({
        rfpFetched: z.number(),
        rfpFetchFailed: z.boolean(), // <-- add this
        ifcSpacesFetched: z.number(),
        uniqueRoomNumbers: z.number(),
        flagsResetRoomsMatched: z.number(), // modifiedCount från updateMany
        upsertOps: z.number(),
    }),
});
export const ZRoomSyncTrigger = z.enum(["manual", "scheduled", "queue"]);
export const ZProjectRunStepStatus = z.enum(["running", "succeeded", "failed", "pending"]);
export const ZRoomSyncRunError = z.object({
    origin: z.enum(["upstream", "config", "network", "db", "unknown"]).optional(),
    code: z.string().optional(),
    message: z.string(),
    stack: z.string().optional(),
});
export const ZRoomSyncRun = z.object({
    _id: ZMongoId.optional(),
    projectNo: ZProjectNo,
    // Hvorfor/av hvem
    trigger: ZRoomSyncTrigger,
    requestedBy: z.string().nullable().optional(), // userId/email (manual)
    correlationId: z.string().nullable().optional(), // optional: for tracing across systems
    // Timestamps
    createdAt: ZDateTime,
    startedAt: ZDateTime.optional(),
    finishedAt: ZDateTime.optional(),
    elapsedMs: z.number().int().nonnegative().optional(),
    // Status
    status: ZProjectRunStepStatus,
    // Input (så du kan se hva som ble kjørt)
    input: z.object({
        // keep open for future params
        projectNo: ZProjectNo,
    }),
    // Output (svaret fra syncRoomsCore)
    result: ZRoomSyncStats.optional(),
    // Error if failed
    error: ZRoomSyncRunError.optional(),
    // Optional: runtime metadata (gold when debugging)
    meta: z
        .object({
        functionName: z.string().optional(),
        invocationId: z.string().optional(),
        hostInstanceId: z.string().optional(),
        env: z.string().optional(), // dev/prod
        version: z.string().optional(), // git sha / build id
    })
        .optional(),
});
/** Vad är detta för run? (framtidssäkert) */
export const ZProjectRunType = z.enum([
    "syncPipeline", // rooms-sync + enqueue + enrich
    // framtida:
    // "drofusSync",
    // "mcbimImport",
]);
export const ZProjectRunTrigger = z.enum(["manual", "scheduled", "queue"]);
export const ZProjectRunStatus = z.enum(["running", "succeeded", "failed", "canceled", "queued"]);
export const ZStepStatus = z.enum(["pending", "running", "succeeded", "failed", "skipped", "queued"]);
export const ZRunError = z.object({
    origin: z.enum(["upstream", "config", "network", "db", "unknown"]).optional(),
    code: z.string().or(z.number()).optional(),
    message: z.string(),
    stack: z.string().optional(),
});
/** Steg 1: rooms-sync */
export const ZRunStepRoomsSync = z.object({
    status: ZStepStatus,
    startedAt: ZDateTime.optional(),
    finishedAt: ZDateTime.optional(),
    elapsedMs: z.number().int().nonnegative().optional(),
    result: ZRoomSyncStats.optional(),
    error: ZRunError.optional(),
});
/** Steg 2: enqueue enrich */
export const ZRunStepEnqueueEnrich = z.object({
    status: ZStepStatus,
    startedAt: ZDateTime.optional(),
    finishedAt: ZDateTime.optional(),
    elapsedMs: z.number().int().nonnegative().optional(),
    jobTypes: z.array(ZJobType).optional(),
    queuedBatches: z.number().int().nonnegative().optional(),
    totalRooms: z.number().int().nonnegative().optional(),
    error: ZRunError.optional(),
});
/** Steg 3: enrich progress */
export const ZRunStepEnrich = z.object({
    status: ZStepStatus,
    // Frozen on the first enrich attempt so redelivery cannot select another 50 rooms.
    mlCeilingRoomNumbers: ZMlCeilingRoomNumbers.optional(),
    startedAt: ZDateTime.optional(),
    finishedAt: ZDateTime.optional(),
    elapsedMs: z.number().int().nonnegative().optional(),
    // progress
    total: z.number().int().nonnegative().optional(),
    done: z.number().int().nonnegative().optional(),
    failed: z.number().int().nonnegative().optional(),
    // valfritt: små sammanfattningar
    lastUpdatedAt: ZDateTime.optional(),
    error: ZRunError.optional(),
});
export const ZRunStepSpecifiedArticlesSyncStats = z.object({
    fetchedRows: z.number().int().nonnegative(),
    uniqueRoomNumbers: z.number().int().nonnegative(),
    resetMatchedRooms: z.number().int().nonnegative(),
    bulkOps: z.number().int().nonnegative(),
});
export const ZRunStepSpecifiedArticlesSync = z.object({
    status: ZProjectRunStepStatus.or(z.literal("skipped")),
    startedAt: ZDateTime.optional(),
    finishedAt: ZDateTime.optional(),
    elapsedMs: z.number().int().nonnegative().optional(),
    result: ZRunStepSpecifiedArticlesSyncStats.optional(),
    error: z
        .object({
        origin: z.string(),
        code: z.string().nullable().optional(),
        message: z.string().nullable().optional(),
        stack: z.string().nullable().optional(),
    })
        .nullable()
        .optional(),
});
export const ZRunStepMcBimItemsSnapshotResult = z.object({
    // how many rooms we expected at start (same as total rooms found in multiplacer.rooms)
    totalExpected: z.number().int().nonnegative().optional(),
    // progress / counters (should end at totalExpected)
    totalRooms: z.number().int().nonnegative().optional(),
    ok: z.number().int().nonnegative().optional(),
    skipped: z.number().int().nonnegative().optional(),
    failed: z.number().int().nonnegative().optional(),
    // perf / debug
    lastBatchMs: z.number().int().nonnegative().optional(),
    avgMsPerRoom: z.number().int().nonnegative().optional(),
    lastUpdatedAt: ZDateTime.optional(),
});
export const ZRunStepMcBimItemsSnapshot = z.object({
    status: ZStepStatus,
    startedAt: ZDateTime.optional(),
    finishedAt: ZDateTime.optional(),
    elapsedMs: z.number().int().nonnegative().optional(),
    result: ZRunStepMcBimItemsSnapshotResult.optional(),
    error: ZRunError.optional(),
});
/** Hela pipeline-runnen */
export const ZProjectRun = z.object({
    roomSyncPlan: ZRoomSyncPlan.optional(),
    _id: ZMongoId.optional(),
    projectNo: ZProjectNo,
    type: ZProjectRunType,
    trigger: ZProjectRunTrigger,
    requestedBy: z.string().optional().nullable(),
    correlationId: z.string().optional().nullable(),
    status: ZProjectRunStatus,
    createdAt: ZDateTime,
    startedAt: ZDateTime.optional(),
    finishedAt: ZDateTime.optional(),
    elapsedMs: z.number().int().nonnegative().optional(),
    steps: z.object({
        roomsSync: ZRunStepRoomsSync.optional(),
        specifiedArticlesSync: ZRunStepSpecifiedArticlesSync.optional(),
        enqueueEnrich: ZRunStepEnqueueEnrich.optional(),
        mcbimItemsSnapshot: ZRunStepMcBimItemsSnapshot.optional(),
        enrich: ZRunStepEnrich.optional(),
    }),
    meta: z
        .object({
        functionName: z.string().optional(),
        invocationId: z.string().optional(),
        hostInstanceId: z.string().optional(),
        env: z.string().optional(),
        version: z.string().optional(),
    })
        .optional(),
});
export const ZProjectRunListResponse = z.object({
    items: z.array(ZProjectRun),
    nextCursor: z.string().optional(),
});
